/**
 * Языки, раскладки и платформы.
 *
 * Прогресс хранится отдельно для каждой пары (язык, раскладка):
 * ТЗ §4.1 FR-WS-02 и §8.1.
 */

/** Поддерживаемые языки обучения. */
export const LANGUAGES = ['ru', 'en'] as const;
export type LanguageCode = (typeof LANGUAGES)[number];

/**
 * Платформа влияет на расположение клавиш, набор специальных символов и
 * модификаторы: русская раскладка Apple расставляет знаки иначе, чем Windows
 * (ТЗ FR-EN-42).
 */
export const OS_PLATFORMS = ['windows', 'macos', 'linux'] as const;
export type OsPlatform = (typeof OS_PLATFORMS)[number];

/**
 * Платформа по умолчанию.
 *
 * Значение нужно там, где система неизвестна: раскладка обязана быть какой-то,
 * а Windows и Linux описываются одними и теми же клавишами, и их больше.
 */
export const DEFAULT_PLATFORM: OsPlatform = 'windows';

/** Физическая форма клавиатуры: влияет на состав клавиш нижнего ряда. */
export const KEYBOARD_FORMS = ['ansi', 'iso'] as const;
export type KeyboardForm = (typeof KEYBOARD_FORMS)[number];

/** Идентификатор раскладки, например `ru-jcuken` или `en-qwerty`. */
export const LAYOUT_IDS = ['en-qwerty', 'ru-jcuken'] as const;
export type LayoutId = (typeof LAYOUT_IDS)[number];

/** Раскладка по умолчанию для языка. */
export const DEFAULT_LAYOUT_BY_LANGUAGE: Readonly<Record<LanguageCode, LayoutId>> = {
  en: 'en-qwerty',
  ru: 'ru-jcuken',
};

/** Полное описание выбранной пользователем клавиатуры. */
export interface KeyboardProfile {
  readonly layoutId: LayoutId;
  readonly platform: OsPlatform;
  readonly form: KeyboardForm;
}

/** Пальцы для указания в подсказках; соответствуют зонам домашнего ряда. */
export const FINGERS = [
  'left-pinky',
  'left-ring',
  'left-middle',
  'left-index',
  'left-thumb',
  'right-thumb',
  'right-index',
  'right-middle',
  'right-ring',
  'right-pinky',
] as const;
export type Finger = (typeof FINGERS)[number];

/** Рука, которой принадлежит палец. */
export type Hand = 'left' | 'right';

/** Определяет руку по пальцу. */
export function handOfFinger(finger: Finger): Hand {
  return finger.startsWith('left') ? 'left' : 'right';
}

/** Проверяет, что значение является поддерживаемым языком. */
export function isLanguageCode(value: string): value is LanguageCode {
  return (LANGUAGES as readonly string[]).includes(value);
}

/** Проверяет, что значение является поддерживаемой раскладкой. */
export function isLayoutId(value: string): value is LayoutId {
  return (LAYOUT_IDS as readonly string[]).includes(value);
}
